import type { SVGProps } from 'react';

/**
 * The House Guide's icon set (docs/GUEST-REDESIGN.md: "one weight of line icon,
 * Lucide-like"). Drawn inline rather than adding the `lucide-react` dependency:
 * a guidebook needs about a dozen glyphs, inline SVG costs nothing to cache and
 * works offline from the first paint, and Section 3 asks for a stated reason
 * before a new dependency. One 24 by 24 grid, one stroke weight, round ends,
 * no fills, so every icon reads the same whether it comes from here or from
 * Lucide later.
 */

const base: SVGProps<SVGSVGElement> = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
  focusable: false,
};

export function WifiIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base} {...props}>
      <path d="M2.5 9a15 15 0 0 1 19 0" />
      <path d="M5.5 12.5a10.5 10.5 0 0 1 13 0" />
      <path d="M8.6 16a6 6 0 0 1 6.8 0" />
      <path d="M12 19.5h.01" />
    </svg>
  );
}

export function CheckoutIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base} {...props}>
      <path d="M9 21H5V3h4" />
      <path d="m16 17 5-5-5-5" />
      <path d="M21 12H9" />
    </svg>
  );
}

export function RulesIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base} {...props}>
      <path d="m5 6 1.5 1.5L9 5" />
      <path d="M12 6.5h8" />
      <path d="m5 12 1.5 1.5L9 11" />
      <path d="M12 12.5h8" />
      <path d="m5 18 1.5 1.5L9 17" />
      <path d="M12 18.5h8" />
    </svg>
  );
}

export function BedIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base} {...props}>
      <path d="M3 20V6" />
      <path d="M3 16h18" />
      <path d="M21 20v-6a3 3 0 0 0-3-3h-8v5" />
      <path d="M6.5 12.6h.01" />
    </svg>
  );
}

export function HouseIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base} {...props}>
      <path d="M3 11.5 12 4l9 7.5" />
      <path d="M5.5 10v10h13V10" />
      <path d="M10 20v-5.5h4V20" />
    </svg>
  );
}

export function PalmIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base} {...props}>
      <path d="M12.5 21c0-4.6.4-7.6 1.8-10.6" />
      <path d="M14.3 10.4c-.9-3-3.8-4.4-7.2-3.6" />
      <path d="M14.3 10.4c.6-3.2 3.4-4.8 7-3.6" />
      <path d="M14.3 10.4c-3-.3-5.4.8-6.6 3.6" />
      <path d="M14.3 10.4c2.4.8 4 2.8 4.2 5.6" />
      <path d="M3 21h18" />
    </svg>
  );
}

export function WavesIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base} {...props}>
      <path d="M2 8c2 0 2 2 4 2s2-2 4-2 2 2 4 2 2-2 4-2 2 2 4 2" />
      <path d="M2 14c2 0 2 2 4 2s2-2 4-2 2 2 4 2 2-2 4-2 2 2 4 2" />
    </svg>
  );
}

export function HotTubIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base} {...props}>
      <path d="M3 15c1.4 1.6 3 1.6 4.4 0s3-1.6 4.4 0 3 1.6 4.4 0 3-1.6 4.4 0" />
      <path d="M4 19h16" />
      <path d="M9 8c-1.2-1.2-1.2-2.8 0-4" />
      <path d="M13 8c-1.2-1.2-1.2-2.8 0-4" />
    </svg>
  );
}

export function SunIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base} {...props}>
      <circle cx="12" cy="12" r="4.5" />
      <path d="M12 2.5v2.5M12 19v2.5M4.6 4.6l1.8 1.8M17.6 17.6l1.8 1.8M2.5 12h2.5M19 12h2.5M4.6 19.4l1.8-1.8M17.6 6.4l1.8-1.8" />
    </svg>
  );
}

export function BoatIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base} {...props}>
      <path d="M3 16h18l-2.4 4H5.4z" />
      <path d="M12 3v13" />
      <path d="M12 4.5c3.4 2 5.2 5.6 5.4 9.5H12" />
      <path d="M10.4 8C8.6 9.6 7.4 11.6 7 14h5" />
    </svg>
  );
}

export function InfoIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base} {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5" />
      <path d="M12 8h.01" />
    </svg>
  );
}

export function MoonIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base} {...props}>
      <path d="M20.5 14A8.5 8.5 0 1 1 10 3.5a7 7 0 0 0 10.5 10.5z" />
    </svg>
  );
}

export function GiftIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base} {...props}>
      <path d="M4 9h16v4H4z" />
      <path d="M5 13v7h14v-7" />
      <path d="M12 9v11" />
      <path d="M12 9c-2.5 0-4-1-4-2.5S9.5 4 12 9zM12 9c2.5 0 4-1 4-2.5S14.5 4 12 9z" />
    </svg>
  );
}

export function SearchIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base} {...props}>
      <circle cx="11" cy="11" r="6.5" />
      <path d="m20 20-4.2-4.2" />
    </svg>
  );
}

export function ChevronIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base} {...props}>
      <path d="m9 5 7 7-7 7" />
    </svg>
  );
}

export function CloseIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base} {...props}>
      <path d="M6 6l12 12M18 6 6 18" />
    </svg>
  );
}
